import type { CertificateRow, FormInput, LabelMap, LabelMode, SearchResponse, ValidationError } from './types';
import { ApiService } from './services/ApiService';
import { ValidationService } from './services/ValidationService';
import { $, hide, setRowVisibility, setText, show } from './utils/DomUtils';

const ERROR_FIELD_TO_CONTAINER: Record<ValidationError['field'], string> = {
    certNo: 'zfpCertNoErrors',
    name: 'zfpNameErrors',
    birthdate: 'zfpBirthdateErrors',
};

export class CertificateSearch {
    private readonly api: ApiService;
    private readonly labelMode: LabelMode = window.zfp_cert_label_mode === 'long' ? 'long' : 'short';
    private readonly labels: LabelMap = window.zfp_cert_labels ?? {};

    private readonly searchForm = $('zfpCertForm');
    private readonly certNoInput = $<HTMLInputElement>('zfpCertNo');
    private readonly nameInput = $<HTMLInputElement>('zfpName');
    private readonly birthdateInput = $<HTMLInputElement>('zfpBirthdate');
    private readonly searchBtn = $<HTMLButtonElement>('zfpSearchBtn');
    private readonly searchBtnLoading = $<HTMLButtonElement>('zfpSearchBtnLoading');
    private readonly searchError = $('zfpSearchError');
    private readonly results = $('zfpResults');
    private readonly certList = $('zfpCertList');
    private readonly noMatch = $('zfpNoMatch');
    private readonly confirmation = $('zfpConfirmation');

    /**
     * Monotonic id of the most recently started lookup (QR auto-lookup or manual
     * search). Since the form stays usable while the QR auto-lookup is in flight,
     * a manual search can overtake it — each async path only applies its response
     * if no newer lookup has started since, so a stale response never overwrites
     * the result (or paints an error over it).
     */
    private lookupSequence = 0;

    constructor() {
        this.api = new ApiService(window.zfp_cert_app_urls);
        this.searchBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            void this.handleSearch();
        });
        this.bindEnterKeySubmit();
        void this.handleQrLookupOnLoad();
    }

    private bindEnterKeySubmit(): void {
        const enter = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                void this.handleSearch();
            }
        };
        this.certNoInput?.addEventListener('keydown', enter);
        this.nameInput?.addEventListener('keydown', enter);
        this.birthdateInput?.addEventListener('keydown', enter);
    }

    /**
     * If the URL carries ?qrId=… (or ?zertifikatId=…, the parameter name printed on the
     * certificates' QR permalinks, #132586), run the QR lookup automatically. The search
     * form stays visible so visitors can still look up other certificates; on success the
     * page scrolls down to the rendered result.
     */
    private async handleQrLookupOnLoad(): Promise<void> {
        const urlParams = new URLSearchParams(window.location.search);
        const qrId = urlParams.get('qrId') ?? urlParams.get('zertifikatId');
        if (!qrId || qrId.trim().length === 0) return;

        const seq = ++this.lookupSequence;
        this.setLoading(true);
        try {
            const response = await this.api.lookupByQrId(qrId);
            if (seq !== this.lookupSequence) return; // a manual search overtook this lookup
            // An empty or failed lookup must NOT read as "certificate not found" — the
            // certificate may well be valid, the QR data source may just be unavailable.
            // Point the visitor to the manual search (form is visible) instead.
            if (response.error || !response.certNo) {
                this.showQrLookupUnavailable();
                return;
            }
            this.renderResult(response);
        } catch {
            if (seq === this.lookupSequence) this.showQrLookupUnavailable();
        } finally {
            if (seq === this.lookupSequence) this.setLoading(false);
        }
    }

    private showQrLookupUnavailable(): void {
        this.showServiceError(ValidationService.label(this.labels, 'error.qrLookup.unavailable'));
    }

    private async handleSearch(): Promise<void> {
        const input: FormInput = {
            certNo: this.certNoInput?.value ?? '',
            name: this.nameInput?.value ?? '',
            birthdate: this.birthdateInput?.value ?? '',
        };
        this.clearAllErrors();
        this.hideResults();

        const errors = ValidationService.validate(input, this.labels);
        if (errors.length > 0) {
            this.showValidationErrors(errors);
            return;
        }

        const seq = ++this.lookupSequence;
        this.setLoading(true);
        try {
            const response = await this.api.searchByForm(input);
            if (seq !== this.lookupSequence) return; // a newer lookup overtook this one
            this.handleResponse(response);
        } catch {
            if (seq === this.lookupSequence) {
                this.showServiceError(ValidationService.label(this.labels, 'error.serviceUnavailable'));
            }
        } finally {
            if (seq === this.lookupSequence) this.setLoading(false);
        }
    }

    private handleResponse(response: SearchResponse): void {
        if (response.error) {
            this.showServiceError(ValidationService.label(this.labels, 'error.serviceUnavailable'));
            return;
        }
        if (!response.certNo) {
            this.showNoMatch();
            return;
        }
        this.renderResult(response);
    }

    private renderResult(info: SearchResponse): void {
        if (!this.results || !this.certList) return;
        const personName = [info.title, info.firstName, info.lastName].filter((p) => p && String(p).trim().length > 0).join(' ');

        setText(this.results, '.zfp-person-name', personName);

        const formattedBirthdate = this.formatDate(info.birthDate ?? '');
        setRowVisibility(this.results, '.zfp-birthdate-row', formattedBirthdate);
        setText(this.results, '.zfp-birthdate', formattedBirthdate);

        const qrImg = this.results.querySelector<HTMLImageElement>('.zfp-qr-img');
        if (qrImg) {
            if (info.qrCodeBase64 && info.qrCodeBase64.length > 0) {
                qrImg.src = `data:image/png;base64,${info.qrCodeBase64}`;
                qrImg.classList.remove('hidden');
            } else if (info.qrUrl && info.qrUrl.length > 0) {
                qrImg.src = info.qrUrl;
                qrImg.classList.remove('hidden');
            } else {
                qrImg.classList.add('hidden');
                qrImg.removeAttribute('src');
            }
        }

        // Replace existing rows (keep the template).
        Array.from(this.certList.querySelectorAll<HTMLElement>('.zfp-cert-row:not([data-template="true"])')).forEach((row) => row.remove());

        const template = this.certList.querySelector<HTMLElement>('.zfp-cert-row[data-template="true"]');
        if (template) {
            (info.certificates ?? []).forEach((row) => {
                const node = template.cloneNode(true) as HTMLElement;
                node.removeAttribute('data-template');
                node.classList.remove('hidden');
                this.populateCertRow(node, row);
                this.certList!.appendChild(node);
            });
        }

        show(this.confirmation);
        show(this.results);
        // Reflect the displayed certificate in the address bar (?qrId=…) so every result —
        // also one found via the manual form — is shareable/bookmarkable and reproduces the
        // same view on reload. replaceState: no history entry, no request, cHash untouched.
        if (info.qrId) {
            const url = new URL(window.location.href);
            url.searchParams.set('qrId', info.qrId);
            url.searchParams.delete('zertifikatId');
            window.history.replaceState(null, '', url);
        }
        // Bring the result into view — both for the QR auto-lookup (form stays above) and
        // for a manual search further down a long page. Position it centered in the upper
        // half of the viewport (a quarter of the free space above it) so the certificate is
        // fully visible; if it is taller than the viewport, keep a small top gap instead.
        // Deferred via setTimeout so the freshly shown result has been laid out first —
        // deliberately not requestAnimationFrame, which never fires in background tabs and
        // would swallow the scroll for QR links opened there.
        window.setTimeout(() => {
            if (!this.results) return;
            const rect = this.results.getBoundingClientRect();
            const gap = Math.max(24, (window.innerHeight - rect.height) / 4);
            window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - gap), behavior: 'smooth' });
        }, 0);
    }

    private populateCertRow(node: HTMLElement, row: CertificateRow): void {
        const levelKeyStr = row.levelKey !== null ? String(row.levelKey) : null;
        setText(node, '.zfp-method', this.pickLabel(row.methodKey, row.methodText));
        setText(node, '.zfp-level', this.pickLabel(levelKeyStr, row.levelText));
        setText(node, '.zfp-basis', row.basis ?? '');
        setText(node, '.zfp-sectors', this.pickLabel(row.sectorKey, row.sectorText));
        setText(node, '.zfp-validuntil', this.formatDate(row.validUntil ?? ''));

        // The API uses "none" / "" as the no-restriction sentinel; show the row only when there is
        // an actual restriction to communicate.
        const restriction = this.pickLabel(row.restrictionKey, row.restrictionText);
        const restrictionDisplay = this.isEmptyish(restriction) ? '' : restriction;
        setRowVisibility(node, '.zfp-restriction-row', restrictionDisplay);
        setText(node, '.zfp-restriction', restrictionDisplay);
    }

    /** Pick the display value depending on labelMode; always falls back to the other one if the preferred is empty. */
    private pickLabel(key: string | null, text: string | null): string {
        const k = key && key.trim().length > 0 ? key.trim() : '';
        const t = text && text.trim().length > 0 ? text.trim() : '';
        if (this.labelMode === 'long') {
            return t.length > 0 ? t : k;
        }
        return k.length > 0 ? k : t;
    }

    private isEmptyish(value: string): boolean {
        const t = value.trim().toLowerCase();
        return t === '' || t === 'none' || t === 'keine' || t === '---';
    }

    /** Accepts both "YYYY-MM-DDTHH:MM:SS" (ZfpApi) and "DD.MM.YYYY" (already formatted). */
    private formatDate(value: string): string {
        if (!value) return '';
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
        return value;
    }

    private setLoading(isLoading: boolean): void {
        if (isLoading) {
            hide(this.searchBtn);
            show(this.searchBtnLoading);
        } else {
            show(this.searchBtn);
            hide(this.searchBtnLoading);
        }
    }

    private hideResults(): void {
        hide(this.results);
        hide(this.noMatch);
    }

    private showNoMatch(): void {
        hide(this.results);
        show(this.noMatch);
    }

    private showServiceError(message: string): void {
        const el = this.searchError;
        if (!el) return;
        const msg = el.querySelector('.zfp-error-message');
        if (msg) msg.textContent = message;
        show(el);
    }

    private clearAllErrors(): void {
        (Object.values(ERROR_FIELD_TO_CONTAINER) as string[]).forEach((id) => {
            const c = $(id);
            if (!c) return;
            c.replaceChildren();
            hide(c);
            c.closest('.form-group')?.classList.remove('error');
        });
        hide(this.searchError);
    }

    private showValidationErrors(errors: ValidationError[]): void {
        errors.forEach((error) => {
            const containerId = ERROR_FIELD_TO_CONTAINER[error.field];
            const container = $(containerId);
            if (!container) return;
            const message = document.createElement('div');
            message.className = 'text-system-failure text-sm mt-2';
            message.textContent = error.message;
            container.replaceChildren(message);
            show(container);
            container.closest('.form-group')?.classList.add('error');
        });
    }
}
